
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PuttSituation } from "@/types/golf";
import { PuttingResult } from "@/types/puttingResults";

interface UseSavePuttingResultsProps {
  situation: PuttSituation;
  scores: Record<string, number[]>;
  onClose: () => void;
  refreshAfterToast?: () => void;
}

export const useSavePuttingResults = ({
  situation,
  scores,
  onClose,
  refreshAfterToast
}: UseSavePuttingResultsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const getTotalPoints = () => {
    return Object.values(scores).reduce((sum, pointScores) => 
      sum + pointScores.reduce((pointSum, score) => pointSum + score, 0), 0
    );
  };

  const getCurrentIndex = (totalPoints: number) => {
    return situation.indexTable.find((entry) => entry.points === totalPoints)?.index ?? 0;
  };

  const saveResults = async () => {
    if (Object.keys(scores).length === 0) {
      onClose();
      return;
    }

    console.log("Sauvegarde des résultats...");
    console.log("Situation:", situation);
    console.log("Scores:", scores);

    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Vous devez être connecté pour sauvegarder vos résultats",
      });
      
      if (refreshAfterToast) {
        refreshAfterToast();
      }
      
      return;
    }

    // Créer un tableau de résultats pour chaque point
    const results = [];
    const optimisticResults: PuttingResult[] = [];
    const now = new Date().toISOString();
    
    for (const [point, pointScores] of Object.entries(scores)) {
      const pointTotal = pointScores.reduce((sum, score) => sum + score, 0);
      const pointIndex = situation.indexTable.find((entry) => entry.points === pointTotal)?.index ?? 0;
      
      // Check if situation has a test_level property
      const hasTestLevel = 'test_level' in situation;
      
      const result = {
        user_id: session.session.user.id,
        situation_id: situation.id,
        points: pointTotal,
        index: pointIndex,
        // Only add test_level if it exists in the situation
        ...(hasTestLevel && { test_level: situation.test_level })
      };
      
      // For optimistic updates
      const optimisticResult: PuttingResult = {
        id: crypto.randomUUID(), // Temporary ID
        user_id: session.session.user.id,
        situation_id: situation.id,
        points: pointTotal,
        index: pointIndex,
        test_date: now,
        test_name: "Test de putting standard",
        // Only add test_level if it exists in the situation
        ...(hasTestLevel && { test_level: situation.test_level as string })
      };
      
      results.push(result);
      optimisticResults.push(optimisticResult);
    }

    console.log("Résultats à insérer:", results);

    try {
      // Determine which query to update based on whether test_level exists
      const queryKey = 'test_level' in situation ? "training-results" : "putting-results";
      
      // Optimistic update - add the results to the cache
      queryClient.setQueryData(
        [queryKey], 
        (oldData: PuttingResult[] | undefined) => 
          oldData ? [...optimisticResults, ...oldData] : optimisticResults
      );

      // Retry logic for insertion
      let retries = 0;
      const maxRetries = 3;
      let success = false;
      
      while (retries < maxRetries && !success) {
        try {
          // Insertion batch dans la table putting_test_results
          const { data, error } = await supabase
            .from('putting_test_results')
            .insert(results);

          if (error) throw error;
          success = true;
        } catch (error) {
          retries++;
          console.error(`Insertion attempt ${retries} failed:`, error);
          
          if (retries < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = 1000 * Math.pow(2, retries - 1);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw error;
          }
        }
      }

      toast({
        title: "Test sauvegardé",
        description: "Vos résultats ont été sauvegardés avec succès",
      });
      
      if (refreshAfterToast) {
        refreshAfterToast();
      }

      // Refresh the cache with the actual data from the server
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      onClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de la sauvegarde des résultats",
      });
      
      if (refreshAfterToast) {
        refreshAfterToast();
      }
      
      // Revert the optimistic update
      queryClient.invalidateQueries({ queryKey: ["putting-results"] });
      queryClient.invalidateQueries({ queryKey: ["training-results"] });
    }
  };

  return {
    saveResults,
    getTotalPoints,
    getCurrentIndex
  };
};
